import { ApolloProvider } from "@apollo/client";
import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  FetchResult,
  fromPromise,
  InMemoryCache,
  NextLink,
  NormalizedCacheObject,
  Operation,
} from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { ErrorResponse, onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";

import { PAGINATED_QUERY_FIELDS } from "./apolloPaginationPolicies";
import { COMPLIANCE_LAYERS_FIELD_POLICY } from "./complianceLayersMerge";
import { periodBucketFor } from "./compliancePeriodBucket";
import { SentryLink } from "apollo-link-sentry";
import * as Device from "expo-device";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { Platform } from "react-native";

import { AnalyticsTracker } from "@/features/analytics";
import { env } from "@/lib/env";

import { getClientBuildId } from "@/config/clientBuildIdentity";
import { DEVICE_TYPE } from "@/config/deviceTypes";
import { useAuth, useCurrentUser } from "@/features/auth";
import { SEARCH_MY_THREADS_FIELD_POLICY } from "@/features/knowledge-graph/utils/provisionalThreads";
import {
  AuthRefreshTokenDocument,
  AuthRefreshTokenMutation,
} from "@/generated/graphql";
import { registerAccessTokenReader } from "@/providers/accessTokenSource";
import { createAppTerminalLink } from "@/providers/batchTerminalLink";
import { createPovTeamHeaderLink } from "@/providers/povTeamHeaderLink";
import { handleTeamSelectionErrors } from "@/providers/teamSelectionErrorHandler";
import { createReadTimeoutRetryLink } from "@/providers/readTimeoutRetry";
import { isAbortError, isTimeoutError } from "@/utils/formatError";
import { ifWeb } from "@/utils/platform";
import removeEmptyKeys from "@/utils/removeEmptyKeys";

/**
 * Props for the ApolloClientProvider component
 */
interface Props {
  children: React.ReactNode;
}

/**
 * Return type for the Apollo Client context
 */
type ReturnType = { client: ApolloClient<NormalizedCacheObject> };

/**
 * Observer interface for GraphQL subscription operations.
 */
export interface SubscriptionObserver<T> {
  closed: boolean;
  next(value: T): void;
  error(errorValue: Error): void;
  complete(): void;
}

/**
 * Represents a queued GraphQL request waiting to be retried after token refresh.
 */
export interface QueuedRequest extends Partial<
  SubscriptionObserver<FetchResult>
> {
  operation: Operation;
  forward?: NextLink;
  subscriber?: SubscriptionObserver<FetchResult>;
}

/** Buffer time before token expiration to trigger proactive refresh (5 minutes) */
export const TOKEN_REFRESH_BUFFER_SECONDS = 300;

/** Delay before retrying queued requests after token refresh (1 second) */
export const RETRY_QUEUE_DELAY_MS = 1000;

/**
 * Extracts the expiration timestamp from a JWT token.
 * @param token - The JWT access token string
 * @returns The exp claim from the token payload, or undefined if parsing fails
 */
export function getExpFromToken(
  token: string | null | undefined
): number | undefined {
  if (token === undefined || token === null) return undefined;
  if (typeof token !== "string" || token === "") return undefined;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return undefined;
    const payload = parts[1];
    // Use atob for browser, Buffer for Node
    const decoded =
      typeof atob !== "undefined"
        ? atob(payload)
        : Buffer.from(payload, "base64").toString();
    const parsed = JSON.parse(decoded);
    return typeof parsed.exp === "number" ? parsed.exp : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Checks if a token is expiring soon based on its expiration timestamp.
 *
 * Returns true if the token expires within TOKEN_REFRESH_BUFFER_SECONDS (5 minutes),
 * or if the token has already expired. Returns false if exp is undefined/null,
 * or if the token expires more than TOKEN_REFRESH_BUFFER_SECONDS from now.
 * @param exp - Token expiration timestamp in seconds since Unix epoch, or undefined
 * @returns True if token is expiring soon or already expired, false otherwise
 */
export function checkIsTokenExpiringSoon(exp: number | undefined): boolean {
  if (!exp) {
    return false;
  }
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return exp - nowInSeconds <= TOKEN_REFRESH_BUFFER_SECONDS;
}

/**
 * Configuration options for creating an ensureFreshToken function.
 */
interface EnsureFreshTokenConfig {
  /** The refresh token used to get a new access token, or undefined if not available */
  refreshToken: string | undefined;
  /** Function that checks if the current access token is expiring soon */
  isTokenExpiringSoon: () => boolean;
  /** Function that performs the token refresh and returns the new access token */
  performRefresh: () => Promise<string>;
  /** Ref holding the current refresh promise, used to prevent concurrent refreshes */
  refreshPromiseRef: { current: Promise<string> | null };
}

/**
 * Gets device information for request headers.
 * @returns Object containing device type and model for native platforms, or empty object for web
 */
export function getDeviceInfo(): Record<string, string | null> {
  const deviceType = DEVICE_TYPE[Device.deviceType || 0];
  const modelDevice = Device?.modelName;

  if (Platform.OS === "web") return {};
  return {
    "x-device-type": deviceType,
    "x-device-model": modelDevice ?? null,
  };
}

/**
 * Factory function that creates an ensureFreshToken async function.
 *
 * The returned function ensures the access token is fresh by proactively refreshing
 * if expiring soon. Uses a shared promise pattern to prevent concurrent refresh calls.
 * Multiple concurrent calls will await the same refresh Promise.
 * @param config - Configuration object with required dependencies
 * @param config.refreshToken - The refresh token, or undefined if not available
 * @param config.isTokenExpiringSoon - Function to check if token is expiring soon
 * @param config.performRefresh - Function to perform the actual token refresh
 * @param config.refreshPromiseRef - Ref to store/track the current refresh promise
 * @returns Async function that ensures the token is fresh before proceeding
 * @remarks This is a pure factory function for testability. The actual useCallback
 * wrapper is in ApolloClientProvider.
 */
export function createEnsureFreshToken(
  config: EnsureFreshTokenConfig
): () => Promise<void> {
  const {
    refreshToken,
    isTokenExpiringSoon,
    performRefresh,
    refreshPromiseRef,
  } = config;

  return async (): Promise<void> => {
    // Early return if no refresh token available
    if (!refreshToken) {
      return;
    }

    // Early return if token is not expiring soon
    if (!isTokenExpiringSoon()) {
      return;
    }

    // If refresh already in progress, wait for it
    if (refreshPromiseRef.current !== null) {
      await refreshPromiseRef.current.catch(() => {
        // Error is already handled by the original caller
      });
      return;
    }

    // Start refresh, store promise so concurrent requests can await it
    refreshPromiseRef.current = performRefresh().finally(() => {
      refreshPromiseRef.current = null;
    });

    await refreshPromiseRef.current.catch(() => {
      // Error is handled by onRefreshTokenError in the refreshToken function
    });
  };
}

/**
 * Checks if a token is expiring soon using the JWT token value with a fallback to auth state exp.
 *
 * Extracts the exp claim from the actual JWT token to avoid stale exp values from
 * auth state that may not update after token refresh. Falls back to authExp when
 * the JWT cannot be parsed.
 * @param currentToken - The current JWT access token string, or null/undefined
 * @param authExp - Fallback expiration timestamp from auth state, used when JWT parsing fails
 * @returns True if the token is expiring soon or already expired
 */
export function checkTokenExpiry(
  currentToken: string | null | undefined,
  authExp: number | undefined
): boolean {
  const tokenExp = getExpFromToken(currentToken);
  const exp = tokenExp ?? authExp;
  return checkIsTokenExpiringSoon(exp);
}

/**
 * Builds request headers by merging existing headers with authorization and device info.
 *
 * Extracted from the component-level getHeaders callback to enable direct testing
 * of the header construction logic without rendering the provider.
 * @param currentToken - The current access token, or null/undefined if not authenticated
 * @param isRefreshing - If true, omits authorization header (prevents circular dependency during token refresh)
 * @param existingHeaders - Existing headers to extend with auth and device info
 * @returns Merged headers object with empty values removed
 * @remarks IDENTITY HEADERS: `authorization` here (and `x-pov-team-id` set by
 * createPovTeamHeaderLink) determine the server-side identity/POV a request
 * executes under. Both are part of {@link buildBatchKey} in
 * providers/batchTerminalLink.ts so queries with different identities are never
 * coalesced into one batched HTTP request. If you add another tenant/identity
 * header, add it to buildBatchKey too or it will leak across batched requests.
 *
 * `x-client-build` is deliberately NOT one of those. It is a property of the
 * CLIENT rather than of the request's identity, so it is the same for every
 * operation this client issues and can never leak anything across a batch.
 * Adding it to buildBatchKey would split batches for no reason (SE-8286).
 */
export function buildRequestHeaders(
  currentToken: string | null | undefined,
  isRefreshing: boolean,
  existingHeaders: Record<string, string>
): Record<string, string | null> {
  const token = currentToken ? `Bearer ${currentToken}` : "";
  const authorization = isRefreshing ? null : token;
  const deviceInfo = getDeviceInfo();

  return removeEmptyKeys({
    ...existingHeaders,
    ...deviceInfo,
    // SE-8286: which JS bundle is running, so a backend log line can tell a
    // live defect apart from a stale client session still on a pre-fix bundle.
    // Those two are indistinguishable without it, which is what made SE-8189
    // cost seven weeks.
    //
    // Set for EVERY platform, unlike getDeviceInfo above, which returns {} on
    // web. A browser tab that is never reloaded never picks up new JS, and that
    // is the stale-session shape SE-8189 actually turned out to be made of —
    // so web is the case this must not skip.
    //
    // getClientBuildId never returns an empty string, which matters because
    // removeEmptyKeys below would drop the header entirely and leave "old
    // client" indistinguishable from "instrumentation broke".
    "x-client-build": getClientBuildId(),
    authorization,
  });
}

/**
 * Resolves a custom cache ID for Apollo InMemoryCache.
 *
 * PlayerBioData uses the "ggPlayerKey" field as its cache ID to properly normalize
 * player bio data. CognitoUser uses a "CognitoUser:{Username}" pattern. All other
 * types fall back to Apollo's default ID resolution.
 * @param object - The GraphQL object being cached, with __typename and fields
 * @param context - The Apollo cache context passed to defaultDataIdFromObject
 * @returns The cache key specifier, cache ID string, or undefined if no ID can be resolved
 */
export function resolveDataId(
  object: Parameters<typeof defaultDataIdFromObject>[0],
  context: Parameters<typeof defaultDataIdFromObject>[1]
): string | string[] | undefined {
  if (object.__typename === "PlayerBioData") {
    return ["ggPlayerKey"];
  }
  if (object.__typename === "CognitoUser") {
    return `CognitoUser:${object.Username}`;
  }
  return defaultDataIdFromObject(object, context);
}

/**
 * Builds the normalized cache key for one `ComplianceOverview` (SE-7675,
 * period bucket added SE-8544).
 *
 * `organizationId` + `scenarioId` is the identity the schema already states,
 * and the reason this is computed rather than declared as
 * `keyFields: ["organizationId", "scenarioId"]` is spelled out on the policy in
 * `createApolloCache()`. For every caller that does not pass a `periodBucket`
 * (every reader except the period-aware `ComplianceLayers` read — see
 * `compliancePeriodBucket.ts`), the key string is byte-identical to the one
 * the pre-SE-8544 two-argument form produced: `JSON.stringify` drops a key
 * whose value is `undefined`, so `periodBucket` never appears in the string
 * unless a genuinely closed quarter supplied one.
 * @param organizationId - The club, non-null on the schema and selected by every
 * operation that reads this type.
 * @param scenarioId - The scenario, or undefined/null for Current Squad
 * actuals. Absent and null are the SAME picture, so both key to null.
 * @param periodBucket - The closed-quarter bucket from
 * {@link periodBucketFor}, or undefined to fold this write onto the ordinary,
 * unbucketed entity — the only value every pre-SE-8544 caller can produce.
 * @returns The cache key, or false to decline to normalize a payload that
 * carries no `organizationId` at all.
 */
export function resolveComplianceOverviewKey(
  organizationId: string | undefined,
  scenarioId: string | undefined,
  periodBucket?: string
): string | false {
  if (organizationId === undefined) return false;
  return `ComplianceOverview:${JSON.stringify({
    organizationId,
    scenarioId: scenarioId ?? null,
    periodBucket,
  })}`;
}

/**
 * Determines if a GraphQL/network error represents an unauthorized (401) response.
 *
 * Checks both the network error's statusCode and the first GraphQL error's
 * extension code to detect authentication failures.
 * @param networkError - The network error from the error response, if any
 * @param graphQLErrors - The GraphQL errors from the error response, if any
 * @returns True if the error indicates an unauthorized request
 */
export function isUnauthorizedError(
  networkError: { statusCode?: number } | null | undefined,
  graphQLErrors: readonly { extensions?: { code?: string } }[] | undefined
): boolean {
  return (
    (networkError != null &&
      "statusCode" in networkError &&
      networkError.statusCode === 401) ||
    graphQLErrors?.[0]?.extensions?.code === "UNAUTHORIZED_EXCEPTION" ||
    false
  );
}

/**
 * Checks if a caught error contains UNAUTHORIZED_EXCEPTION, indicating a permanently invalid token.
 * @param error - The error caught from an Apollo mutation
 */
export function isAuthError(error: unknown): boolean {
  if (error == null || typeof error !== "object" || !("graphQLErrors" in error))
    return false;
  const { graphQLErrors } = error as { graphQLErrors: unknown };
  return (
    Array.isArray(graphQLErrors) &&
    graphQLErrors.some(
      (e: { extensions?: { code?: string } }) =>
        e.extensions?.code === "UNAUTHORIZED_EXCEPTION"
    )
  );
}

/**
 * Determines if token refresh should be skipped for the current error.
 *
 * Skips refresh when the error is not unauthorized, when the operation is
 * AuthSignOut/AuthRefreshToken (to prevent circular refresh), or when no
 * refresh token is available.
 * @param isUnauthorized - Whether the error is an unauthorized error
 * @param operationName - The name of the GraphQL operation that failed
 * @param refreshToken - The current refresh token, or null/undefined if unavailable
 * @returns True if token refresh should be skipped
 */
export function shouldSkipTokenRefresh(
  isUnauthorized: boolean,
  operationName: string | undefined,
  refreshToken: string | null | undefined
): boolean {
  return (
    !isUnauthorized ||
    operationName === "AuthSignOut" ||
    operationName === "AuthRefreshToken" ||
    !refreshToken
  );
}

/**
 * Operations that can proceed without an access token.
 * Auth mutations must bypass the queue to avoid deadlock, and
 * ListFeatureFlags is intentionally public.
 */
const UNAUTHENTICATED_OPERATIONS = new Set([
  "ListFeatureFlags",
  "AuthSignIn",
  "AuthConfirmSignIn",
  "AuthSignOut",
  "AuthRefreshToken",
]);

const ApolloClientContext = React.createContext<ReturnType | undefined>(
  undefined
);

export const useApolloClient = () => React.useContext(ApolloClientContext);

/**
 * Build the app's normalized Apollo cache.
 * @description Extracted from the provider so the cache-identity rules
 * (`keyArgs`) are testable directly — SE-7136 shipped a silent
 * cache-collision because nothing asserted them. The field policies
 * themselves live in `apolloPaginationPolicies.ts` (extracted separately,
 * SE-6975) so this factory stays a thin, testable wrapper rather than a
 * second place those `keyArgs` could drift out of sync.
 * @returns The configured cache.
 */
export function createApolloCache(): InMemoryCache {
  return new InMemoryCache({
    dataIdFromObject: resolveDataId,
    typePolicies: {
      Query: {
        fields: {
          ...PAGINATED_QUERY_FIELDS,
          // SE-8865: shows a finished chat until the server lists it.
          searchMyThreads: SEARCH_MY_THREADS_FIELD_POLICY,
        },
      },
      /**
       * SE-7675. `ComplianceOverview` carries no `id`, so without this it did
       * not normalize: every argument-less read of `complianceOverview` shared
       * one unnormalized `ROOT_QUERY.complianceOverview` value, and Apollo
       * REPLACES an unnormalized object rather than merging it. A
       * `ComplianceViewerTier` answer (two scalars, no `layers`) landing after
       * a `ClubObjectives` answer therefore dropped `layers` outright and
       * blanked the club's Objective cards, announced only by the
       * "Cache data may be lost when replacing the complianceOverview field"
       * warning everyone had learned to ignore.
       *
       * `merge: true` was the obvious alternative and is the weaker one: it
       * closes this case only because the tier read happens not to select
       * `layers`, and leaves the object unnormalized so the four reads keep
       * four divergent copies of one club's picture.
       *
       * The key is `organizationId` + `scenarioId`, which is the identity the
       * schema already states: `organizationId` is non-null and defaults to
       * the caller's own club, `scenarioId` is null for Current Squad actuals
       * and an id for a scenario projection. `asOf` is deliberately NOT in the
       * key — it moves on every request, so keying on it would fragment one
       * entry into an unbounded number of them (the SE-5058 lesson).
       *
       * ## Why a function and not the `["organizationId", "scenarioId"]` array
       *
       * The array specifier is TOTAL: Apollo throws
       * `Missing field 'scenarioId' while extracting keyFields` and writes
       * NOTHING when a payload omits a key field. That is a strictly worse
       * failure than the one being fixed — one absent nullable field takes the
       * whole query result down — and `scenarioId` is exactly the field a
       * hand-built payload forgets, because the operation passes no argument
       * for it. Two e2e `ComplianceViewerTier` stubs omit it today.
       *
       * So the key is computed instead, and an ABSENT `scenarioId` is read as
       * null. That does not fragment anything: absent and null denote the same
       * Current Squad picture, so both land on one entity. It produces the
       * byte-identical key string the array specifier would, which
       * `ApolloClient.complianceOverviewIdentity.test.ts` asserts literally.
       *
       * `organizationId` is non-null on the schema and selected by every
       * operation. If it is genuinely absent the payload is malformed, and the
       * conservative answer is to decline to normalize — degrading to the old
       * behaviour for that one write rather than throwing away the result.
       *
       * This normalizes the PARENT only. `ComplianceLayerGroup` and
       * `ComplianceRuleEvaluation` still have no identity, so `layers` would
       * be replaced wholesale by the later write. SE-8547 gives it a keyed
       * merge instead, because the Compliance tab mounts three readers of it
       * whose selections no longer nest and they refetched each other forever
       * — see `complianceLayersMerge.ts`.
       *
       * SE-8544 adds a fourth key component, `periodBucket`: the KPI grid's
       * period selector reads a CLOSED quarter's `ComplianceLayers` picture by
       * passing `asOf`, and without this that write would land on the exact
       * SAME entity as the current picture and get merged into it field by
       * field (`complianceLayersMerge.ts`'s `layers` merge), silently
       * blending two different quarters' rule evaluations together. The
       * bucket comes from the RESPONSE's own `asOf` field via `readField`,
       * never from the operation's variables — `KeyFieldsContext` exposes no
       * `variables` — and it is `undefined` for every reader that does not
       * select `asOf` at all (Tier, ClubObjectives, PlanStatus) or whose
       * `asOf` falls in the quarter still in progress, so none of them forks
       * off this shared entity. See `compliancePeriodBucket.ts` for why a
       * quarter bucket is safe where a raw `asOf` timestamp was rejected
       * (SE-5058) — bucketed granularity does not fragment, day-to-day
       * clock movement does.
       */
      ComplianceOverview: {
        keyFields: (object, { readField }) =>
          resolveComplianceOverviewKey(
            readField<string>("organizationId", object),
            readField<string>("scenarioId", object),
            periodBucketFor(readField<string>("asOf", object))
          ),
        fields: {
          layers: COMPLIANCE_LAYERS_FIELD_POLICY,
        },
      },
    },
  });
}

/**
 * Provider component that initializes and provides Apollo Client instance to the application.
 * @param root0 - Component props
 * @param root0.children - React children nodes to render within the provider
 */
const ApolloClientProvider = ({ children }: Props) => {
  const auth = useAuth();
  const accessToken = auth?.data?.accessToken;
  const isAuthLoaded = auth?.isLoaded ?? false;
  const operationQueue = useRef<QueuedRequest[]>([]);

  /**
   * Ref holding the current access token.
   * Used to avoid stale closure issues in Apollo Link callbacks.
   * Synced eagerly during render so child components' queries (e.g. AuthMe)
   * see the latest value before parent useLayoutEffect fires (React fires
   * layout effects bottom-up: child before parent).
   */
  const accessTokenRef = useRef(accessToken);
  // eslint-disable-next-line react-hooks/refs -- Read only in async link callbacks, not during render
  accessTokenRef.current = accessToken;

  /**
   * Ref holding the auth loaded state.
   * Used to avoid stale closure issues in Apollo Link callbacks.
   * Synced eagerly during render for the same reason as accessTokenRef.
   */
  const isAuthLoadedRef = useRef(isAuthLoaded);
  // eslint-disable-next-line react-hooks/refs -- Read only in async link callbacks, not during render
  isAuthLoadedRef.current = isAuthLoaded;

  /**
   * Ref holding the promise resolver for when auth becomes ready.
   * Used by authQueueLink to wait for auth to load before forwarding requests.
   * @remarks Set when auth is not loaded, called when auth loads to flush queued requests.
   */
  const authReadyResolversRef = useRef<(() => void)[]>([]);

  /**
   * Ref holding the current token refresh promise.
   * Used to prevent concurrent refresh calls and allow multiple requests to await the same refresh.
   * @remarks When null, no refresh is in progress. When set, contains the active refresh Promise.
   */
  const refreshPromiseRef = useRef<Promise<string> | null>(null);
  /**
   * Ref holding the Apollo Client instance.
   * Used to enable proactive token refresh in authLink before the client is fully initialized.
   * @remarks Set after client creation, used by ensureFreshToken to perform refresh mutations.
   */
  const clientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(null);
  const currentUser = useCurrentUser();

  /**
   * Ref holding the ensureFreshToken callback.
   * Used by authLink to always call the latest version without recreating the link.
   * @remarks Updated via useLayoutEffect to sync with the latest callback.
   */
  const ensureFreshTokenRef = useRef<() => Promise<void>>(() =>
    Promise.resolve()
  );

  /**
   * Ref holding the refreshToken callback.
   * Used by errorLink to always call the latest version without recreating the link.
   * @remarks Updated via useLayoutEffect to sync with the latest callback.
   */
  const refreshTokenRef = useRef<
    (client: ApolloClient<NormalizedCacheObject>) => Promise<string>
  >(() => Promise.resolve(""));

  /**
   * Ref holding the auth refresh token string.
   * Used by errorLink to check token availability without recreating the link.
   * @remarks Updated via useLayoutEffect to sync with auth state.
   */
  const authRefreshTokenRef = useRef(auth?.data?.refreshToken);

  /**
   * Ref holding the retryQueuedRequests callback.
   * Used by errorLink to retry queued requests without recreating the link.
   * @remarks Updated via useLayoutEffect to sync with the latest callback.
   */
  const retryQueuedRequestsRef = useRef<() => void>(() => undefined);

  /**
   * Checks if the current access token is expiring soon.
   * @returns True if the token expires within TOKEN_REFRESH_BUFFER_SECONDS, false otherwise
   * @remarks Uses getExpFromToken to extract exp from the actual JWT token,
   * avoiding stale exp values from auth state that don't update after refresh.
   */
  const _isTokenExpiringSoon = useCallback(
    (): boolean => checkTokenExpiry(accessTokenRef.current, auth?.data?.exp),
    [auth?.data?.exp]
  );

  /**
   * Handles refresh token errors by logging out only on genuine auth failures.
   * Transient errors (network, server) are ignored so the next request can retry.
   * @param error - The error caught from the refresh mutation
   * @remarks
   * onRefreshTokenError returns Promise<void> (see features/auth/providers/Auth.tsx).
   * It is awaited here to prevent a floating promise, and .catch(() => undefined)
   * ensures that a rejection in onRefreshTokenError (e.g. a failed sign-out navigation)
   * does not propagate as an unhandled rejection to the caller.
   */
  const handleRefreshError = useCallback(
    async (error: unknown): Promise<void> => {
      if (!isAuthError(error)) return;
      void AnalyticsTracker.user.logUserSignOut({
        user_id: currentUser?.id ?? "",
        organization_id: currentUser?.organizationId ?? "",
        phone_number: currentUser?.phoneNumber ?? "",
        device_name: Device?.modelName ?? "",
        device_os: Device?.osName ?? "",
        device_type: ifWeb("web", "mobile"),
        platform: Platform.OS,
        reason: "refresh_token_revoked",
        timestamp: new Date().toISOString(),
      });
      await auth
        ?.onRefreshTokenError(
          error instanceof Error ? error : new Error("Unknown error")
        )
        .catch(() => undefined);
    },
    [
      auth,
      currentUser?.id,
      currentUser?.organizationId,
      currentUser?.phoneNumber,
    ]
  );

  /**
   * Performs token refresh using the Apollo Client.
   * @returns Promise resolving to the new access token, or empty string on error
   * @remarks This is used by ensureFreshToken for proactive token refresh
   */
  const performRefresh = useCallback(async (): Promise<string> => {
    if (!clientRef.current) {
      return "";
    }
    try {
      const res = await clientRef.current.mutate<AuthRefreshTokenMutation>({
        mutation: AuthRefreshTokenDocument,
        variables: { input: { refreshToken: auth?.data?.refreshToken } },
      });
      const newAccessToken = res.data?.refreshToken?.data?.accessToken ?? "";

      // CRITICAL: Update the ref IMMEDIATELY so pending requests use the fresh token
      // Don't wait for React re-render cycle
      if (newAccessToken) {
        accessTokenRef.current = newAccessToken;
      }

      // Also update React state for persistence
      await auth?.onRefreshToken(newAccessToken);
      return newAccessToken;
    } catch (error) {
      await handleRefreshError(error);
      return "";
    }
  }, [auth, handleRefreshError]);

  /**
   * Ensures the access token is fresh before making requests.
   * Proactively refreshes the token if it's expiring soon.
   * @remarks Uses shared promise pattern to prevent concurrent refresh calls.
   * Multiple concurrent calls will await the same refresh Promise.
   */
  const ensureFreshToken = useCallback(() => {
    return createEnsureFreshToken({
      refreshToken: auth?.data?.refreshToken ?? undefined,
      isTokenExpiringSoon: _isTokenExpiringSoon,
      performRefresh,
      refreshPromiseRef,
    })();
  }, [auth?.data?.refreshToken, _isTokenExpiringSoon, performRefresh]);

  /**
   * Refreshes the access token using the refresh token.
   * @param client - The Apollo Client instance
   * @returns A promise that resolves to the new access token or empty string on failure
   */
  const refreshToken = useCallback(
    async (client: ApolloClient<NormalizedCacheObject>): Promise<string> => {
      try {
        const res = await client.mutate<AuthRefreshTokenMutation>({
          mutation: AuthRefreshTokenDocument,
          variables: { input: { refreshToken: auth?.data?.refreshToken } },
        });
        const newAccessToken = res.data?.refreshToken?.data?.accessToken ?? "";

        // CRITICAL: Update the ref IMMEDIATELY so pending requests use the fresh token
        // Don't wait for React re-render cycle
        if (newAccessToken) {
          accessTokenRef.current = newAccessToken;
        }

        await auth?.onRefreshToken(newAccessToken);
        return newAccessToken;
      } catch (error) {
        await handleRefreshError(error);
        return "";
      }
    },
    [auth, handleRefreshError]
  );

  /**
   * Constructs request headers including authorization and device info.
   * @param headers - Existing headers to extend
   * @param isRefreshing - If true, omits authorization header (for refresh token requests)
   * @returns Promise resolving to headers object with empty values removed
   * @remarks Uses accessTokenRef to avoid stale closure issues in Apollo Link callbacks.
   */
  const getHeaders = useCallback(
    (
      headers: Record<string, string>,
      isRefreshing = false
    ): Promise<Record<string, string | null>> =>
      Promise.resolve(
        buildRequestHeaders(accessTokenRef.current, isRefreshing, headers)
      ),
    []
  );

  /**
   * Apollo Link that gates requests on auth state.
   * Queues requests while auth is loading from AsyncStorage, then either
   * forwards (if a token is present) or silently drops (if unauthenticated).
   * Operations in UNAUTHENTICATED_OPERATIONS always pass through.
   * @remarks Prevents wasted 401 requests from components that mount before
   * the routing guard hides them (e.g. dashboard widgets on the sign-in screen).
   */
  /* eslint-disable react-hooks/refs -- False positive: ApolloLink callback executes during
     network requests, not during render. The linter cannot distinguish deferred execution. */
  const authQueueLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        const operationName = operation.operationName;
        const isPublicOperation = UNAUTHENTICATED_OPERATIONS.has(operationName);

        // If auth is already loaded, check for token before forwarding
        if (isAuthLoadedRef.current) {
          // Always forward public operations and authenticated requests
          if (isPublicOperation || accessTokenRef.current) {
            return forward(operation);
          }
          // Drop unauthenticated requests for protected operations
          return new Observable(observer => {
            observer.complete();
          });
        }

        // Queue the request until auth is ready
        return new Observable(observer => {
          /**
           * Resolver function called when auth becomes ready.
           * Forwards only if authenticated or if the operation is public.
           */
          const onAuthReady = (): void => {
            if (isPublicOperation || accessTokenRef.current) {
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            } else {
              observer.complete();
            }
          };

          authReadyResolversRef.current.push(onAuthReady);
        });
      }),
    []
  );
  /* eslint-enable react-hooks/refs -- End authQueueLink */

  /**
   * Waits for an in-flight refresh, or starts one if the token is expiring.
   * @remarks Shared by authLink and the SSE token reader (SE-8687) so both
   * join the same refresh promise instead of racing a second refresh. Only
   * called at request time, never during render.
   */
  const awaitFreshToken = useCallback(async (): Promise<void> => {
    // If a refresh is in progress (even if this closure doesn't have refreshToken),
    // wait for it to complete before proceeding
    if (refreshPromiseRef.current) {
      await refreshPromiseRef.current.catch(() => {
        // Error handled elsewhere
      });
      return;
    }
    // No refresh in progress, check if we need to start one
    await ensureFreshTokenRef.current();
  }, []);

  /**
   * Apollo Link that adds authorization headers and proactively refreshes tokens.
   * @remarks Reads from refs to always get current token value, avoiding stale closures.
   * Skips token refresh for AuthRefreshToken to avoid circular dependency.
   */
  /* eslint-disable react-hooks/refs -- False positive: setContext callback executes during
     network requests when headers are being set, not during render. */
  const authLink = useMemo(
    () =>
      setContext(async (request, { headers }) => {
        const isRefreshingOperation =
          request.operationName === "AuthRefreshToken";

        // Skip ensureFreshToken for the refresh mutation itself to avoid circular dependency
        if (!isRefreshingOperation) {
          await awaitFreshToken();
        }

        const extendedHeaders = await getHeaders(
          headers,
          isRefreshingOperation
        );

        return { headers: extendedHeaders };
      }),
    [awaitFreshToken, getHeaders]
  );
  /* eslint-enable react-hooks/refs -- End authLink */

  /**
   * Apollo Link that handles GraphQL and network errors.
   * Uses shared promise pattern to prevent concurrent token refresh calls.
   * @remarks Kept as fallback for edge cases (clock skew, server-side invalidation).
   */
  /* eslint-disable react-hooks/refs, react-hooks/preserve-manual-memoization -- False positive: onError callback executes when
     GraphQL/network errors occur, not during render. */
  const errorLink = useMemo(
    () =>
      onError(
        ({
          graphQLErrors,
          networkError,
          operation,
          forward,
        }: ErrorResponse) => {
          // A client-side request timeout (SE-5055). Unlike an intentional
          // abort, a timeout is a genuine failure: return without swallowing
          // so it propagates to `query.error` and the UI can show an error +
          // retry affordance. This guard MUST stay strictly before the abort
          // guard below, because a timeout aborts the request and would
          // otherwise be misclassified as a self-cancel.
          if (isTimeoutError(networkError)) {
            return;
          }

          // A request the client aborted itself — most commonly an in-flight
          // query cancelled by `apolloClient.resetStore()` during an
          // active-team POV switch, or a superseded typeahead request. It is
          // not a real failure: swallow it as an empty result so component
          // `onError` handlers don't raise a spurious error toast (SE-4940).
          // The team switch and resetStore-driven refetches still succeed.
          if (isAbortError(networkError)) {
            return Observable.of<FetchResult>({ data: null });
          }

          handleTeamSelectionErrors(graphQLErrors, operation.operationName);

          const unauthorized = isUnauthorizedError(
            networkError as { statusCode?: number } | null | undefined,
            graphQLErrors as
              | readonly { extensions?: { code?: string } }[]
              | undefined
          );

          if (
            shouldSkipTokenRefresh(
              unauthorized,
              operation.operationName,
              authRefreshTokenRef.current
            )
          ) {
            return;
          }

          // Use shared promise - either join existing refresh or start new one
          const refreshPromise =
            refreshPromiseRef.current ??
            refreshTokenRef.current(clientRef.current!);

          if (!refreshPromiseRef.current) {
            refreshPromiseRef.current = refreshPromise;
            refreshPromise.finally(() => {
              refreshPromiseRef.current = null;
            });
          }

          return fromPromise(
            refreshPromise
              .then(() => getHeaders(operation.getContext().headers))
              .then(newHeaders => {
                operation.setContext({ headers: newHeaders });
                return true;
              })
              .catch(() => {
                operationQueue.current = [];
                return false;
              })
              .finally(() => {
                retryQueuedRequestsRef.current();
              })
          )
            .filter(Boolean)
            .flatMap(() => forward(operation));
        }
      ),
    [getHeaders]
  );
  /* eslint-enable react-hooks/refs, react-hooks/preserve-manual-memoization -- End errorLink */

  /**
   * Retries all queued operations after a delay.
   * @remarks Uses map instead of forEach for immutable pattern.
   * Clears the queue before processing to prevent duplicate retries.
   */
  const retryQueuedRequests = useCallback(() => {
    setTimeout(() => {
      const queue = operationQueue.current;
      operationQueue.current = [];

      queue.forEach(({ operation, forward, subscriber }) => {
        const headers = operation.getContext().headers;
        getHeaders(headers).then(newHeaders => {
          operation.setContext({ headers: newHeaders });
          forward?.(operation).subscribe(subscriber!);
        });
      });
    }, RETRY_QUEUE_DELAY_MS);
  }, [getHeaders]);

  const cache = useMemo(() => createApolloCache(), []);

  /* eslint-disable react-hooks/preserve-manual-memoization -- Apollo client construction is intentionally memoized; React Compiler's preservation check trips on the link-array spread but the memoization is correct. */
  const client = useMemo(() => {
    return new ApolloClient({
      defaultOptions: { watchQuery: { errorPolicy: "all" } },
      link: ApolloLink.from([
        authQueueLink,
        // Inject `x-pov-team-id` header for demo-flex orgs. Sits AFTER the
        // auth queue (so unauthenticated ops are dropped first) and BEFORE
        // authLink (header is set whether or not we end up adding auth to
        // the request). Hidden-mode orgs short-circuit it as a no-op.
        createPovTeamHeaderLink(),
        // SE-6450: heals dead-socket evictions (backend SE-5608) by
        // retrying idempotent queries once — see providers/readTimeoutRetry.
        // SE-7338 adds a second arm to the same link: a query the CLIENT
        // aborted at its own transport budget is re-issued once, UNBATCHED, so
        // one slow member of a coalesced POST can no longer end every
        // co-batched query's life for the rest of the session.
        // Sits BEFORE authLink so `forward(operation)` on retry re-runs
        // authLink and picks up current/refreshed auth headers, instead of
        // resuming at errorLink with the stale headers from the failed
        // attempt (CodeRabbit review on PR #6369).
        createReadTimeoutRetryLink(),
        authLink,
        errorLink,
        new SentryLink(),
        // Terminal transport: queries are coalesced via BatchHttpLink to
        // collapse the initial-render request fan-out into fewer HTTP round
        // trips; mutations and subscriptions stay on a plain unbatched
        // HttpLink. Each link gets its own SE-5055 timeout fetch: the unbatched
        // link uses the 15s single-op budget, while the batched query link uses
        // a longer 25s budget — a batched POST's duration is the slowest of its
        // N co-batched queries, so a shared 15s timer would abort the whole
        // batch and blank the page under congestion (SE-5038). Heavy pages go
        // out alone on a third link that keeps the 25s budget (SE-8579). See
        // providers/batchTerminalLink.ts.
        createAppTerminalLink(env.EXPO_PUBLIC_GRAPHQL_BASE_URL),
      ]),
      cache,
    });
  }, [authQueueLink, authLink, cache, errorLink]);

  const value = useMemo(() => ({ client }), [client]);
  /* eslint-enable react-hooks/preserve-manual-memoization -- End client / value memoization. */

  // Store client reference via useLayoutEffect for ensureFreshToken to use
  useLayoutEffect(() => {
    clientRef.current = client;
  }, [client]);

  // accessTokenRef and isAuthLoadedRef are synced eagerly during render (see declarations above)
  // to avoid race conditions where child queries fire before parent useLayoutEffect

  useLayoutEffect(() => {
    ensureFreshTokenRef.current = ensureFreshToken;
  }, [ensureFreshToken]);

  useLayoutEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  useLayoutEffect(() => {
    authRefreshTokenRef.current = auth?.data?.refreshToken;
  }, [auth?.data?.refreshToken]);

  useLayoutEffect(() => {
    retryQueuedRequestsRef.current = retryQueuedRequests;
  }, [retryQueuedRequests]);

  // SE-8687: the Ask Gemini SSE streams bypass authLink, so hand them the same
  // wait-or-refresh step through the module registry and read the token after it.
  useLayoutEffect(
    () =>
      registerAccessTokenReader(async () => {
        await awaitFreshToken();
        return accessTokenRef.current ?? undefined;
      }),
    [awaitFreshToken]
  );

  // Flush auth queue when auth becomes loaded
  useEffect(() => {
    if (isAuthLoaded && authReadyResolversRef.current.length > 0) {
      const resolvers = authReadyResolversRef.current;
      authReadyResolversRef.current = [];
      resolvers.forEach(resolve => resolve());
    }
  }, [isAuthLoaded, accessToken]);

  return (
    <ApolloClientContext.Provider value={value}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </ApolloClientContext.Provider>
  );
};

export { ApolloClientContext, ApolloClientProvider };
